import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import {
    MagnifyingGlassIcon,
    ChartBarIcon,
    UserGroupIcon,
    CurrencyDollarIcon,
    ShoppingBagIcon,
    CalendarIcon,
    ArrowTrendingUpIcon,
    UserIcon,
    FunnelIcon,
    ChevronDownIcon,
    ChevronUpIcon
} from '@heroicons/react/24/outline';
import { StarIcon } from '@heroicons/react/24/solid';

// Customer Card Component
function CustomerCard({ customer, onViewDetails }) {
    const formatDate = (dateString) => {
        if (!dateString) return 'N/A';
        return new Date(dateString).toLocaleDateString();
    };

    const daysSinceLastPurchase = (dateString) => {
        if (!dateString) return 'N/A';
        const lastPurchase = new Date(dateString);
        const today = new Date();
        const diffTime = Math.abs(today - lastPurchase);
        const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
        return diffDays;
    };

    const getSegmentColor = (segment) => {
        switch (segment) {
            case 'VIP':
                return 'bg-purple-100 text-purple-800';
            case 'Regular':
                return 'bg-blue-100 text-blue-800';
            case 'New':
                return 'bg-green-100 text-green-800';
            case 'Inactive':
                return 'bg-gray-100 text-gray-800';
            default:
                return 'bg-gray-100 text-gray-800';
        }
    };

    return (
        <div className="bg-white rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 overflow-hidden">
            <div className="p-6">
                <div className="flex items-center justify-between mb-4">
                    <div>
                        <h3 className="text-lg font-semibold text-gray-900">{customer.name}</h3>
                        <p className="text-sm text-gray-600">{customer.email}</p>
                    </div>
                    <span className={`px-3 py-1 rounded-full text-xs font-medium ${getSegmentColor(customer.segment)}`}>
                        {customer.segment}
                    </span>
                </div>

                <div className="grid grid-cols-2 gap-4 mb-4">
                    <div>
                        <p className="text-sm text-gray-500">Total Spent</p>
                        <p className="text-lg font-semibold">¥{customer.total_spent}</p>
                    </div>
                    <div>
                        <p className="text-sm text-gray-500">Orders</p>
                        <p className="text-lg font-semibold">{customer.orders_count}</p>
                    </div>
                </div>

                <div className="mb-4">
                    <p className="text-sm text-gray-500">Last Purchase</p>
                    <div className="flex items-center justify-between">
                        <p className="text-sm font-medium">{formatDate(customer.last_purchase)}</p>
                        <span className="text-xs text-gray-500">
                            {daysSinceLastPurchase(customer.last_purchase)} days ago
                        </span>
                    </div>
                </div>

                <div className="mb-4">
                    <p className="text-sm text-gray-500">Preferred Brands</p>
                    <div className="flex flex-wrap gap-1 mt-1">
                        {customer.preferred_brands.map((brand, index) => (
                            <span
                                key={index}
                                className="px-2 py-1 bg-gray-100 text-gray-700 text-xs rounded-full"
                            >
                                {brand}
                            </span>
                        ))}
                    </div>
                </div>

                <button
                    onClick={() => onViewDetails(customer)}
                    className="w-full bg-blue-600 hover:bg-blue-700 text-white py-2 px-4 rounded-lg font-medium transition-colors"
                >
                    View Details
                </button>
            </div>
        </div>
    );
}

// Customer Details Modal Component
function CustomerDetailsModal({ customer, isOpen, onClose }) {
    const [customerDetails, setCustomerDetails] = useState(null);
    const [activeTab, setActiveTab] = useState('overview');
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        if (isOpen && customer) {
            fetchCustomerDetails();
        }
    }, [isOpen, customer]);

    const fetchCustomerDetails = async () => {
        setLoading(true);
        try {
            const backendUrl = process.env.REACT_APP_BACKEND2_BASE_URL || 'https://suanfamama-fashion-buyer-production.up.railway.app';
            const response = await fetch(`${backendUrl}/customers/${encodeURIComponent(customer.id)}`);
            if (response.ok) {
                const data = await response.json();
                setCustomerDetails(data);
            }
        } catch (error) {
            console.error('Error fetching customer details:', error);
        } finally {
            setLoading(false);
        }
    };

    const formatDate = (dateString) => {
        if (!dateString) return 'N/A';
        return new Date(dateString).toLocaleDateString();
    };

    const getSegmentColor = (segment) => {
        switch (segment) {
            case 'VIP':
                return 'bg-purple-100 text-purple-800';
            case 'Regular':
                return 'bg-blue-100 text-blue-800';
            case 'New':
                return 'bg-green-100 text-green-800';
            case 'Inactive':
                return 'bg-gray-100 text-gray-800';
            default:
                return 'bg-gray-100 text-gray-800';
        }
    };

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
            <div className="bg-white rounded-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
                {/* Header */}
                <div className="bg-gradient-to-r from-blue-600 to-purple-600 p-6 text-white">
                    <div className="flex items-center justify-between">
                        <div>
                            <h2 className="text-2xl font-bold">{customer.name}</h2>
                            <p className="text-blue-100">{customer.email}</p>
                        </div>
                        <button
                            onClick={onClose}
                            className="text-white hover:text-gray-200 text-2xl font-bold"
                        >
                            ×
                        </button>
                    </div>
                </div>

                {/* Tabs */}
                <div className="border-b border-gray-200">
                    <nav className="flex -mb-px">
                        <button
                            onClick={() => setActiveTab('overview')}
                            className={`py-4 px-6 font-medium text-sm ${activeTab === 'overview'
                                ? 'border-b-2 border-blue-500 text-blue-600'
                                : 'text-gray-500 hover:text-gray-700'
                                }`}
                        >
                            Overview
                        </button>
                        <button
                            onClick={() => setActiveTab('purchases')}
                            className={`py-4 px-6 font-medium text-sm ${activeTab === 'purchases'
                                ? 'border-b-2 border-blue-500 text-blue-600'
                                : 'text-gray-500 hover:text-gray-700'
                                }`}
                        >
                            Purchase History
                        </button>
                        <button
                            onClick={() => setActiveTab('analytics')}
                            className={`py-4 px-6 font-medium text-sm ${activeTab === 'analytics'
                                ? 'border-b-2 border-blue-500 text-blue-600'
                                : 'text-gray-500 hover:text-gray-700'
                                }`}
                        >
                            Analytics
                        </button>
                    </nav>
                </div>

                {/* Content */}
                <div className="p-6">
                    {loading ? (
                        <div className="flex items-center justify-center py-12">
                            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
                        </div>
                    ) : customerDetails ? (
                        <>
                            {activeTab === 'overview' && (
                                <div className="space-y-6">
                                    {/* Customer Info */}
                                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                                        <div className="bg-gray-50 p-4 rounded-lg">
                                            <p className="text-sm text-gray-500">Segment</p>
                                            <span className={`px-3 py-1 rounded-full text-xs font-medium ${getSegmentColor(customerDetails.customer.segment)}`}>
                                                {customerDetails.customer.segment}
                                            </span>
                                        </div>
                                        <div className="bg-gray-50 p-4 rounded-lg">
                                            <p className="text-sm text-gray-500">Total Spent</p>
                                            <p className="text-lg font-semibold">¥{customerDetails.customer.total_spent}</p>
                                        </div>
                                        <div className="bg-gray-50 p-4 rounded-lg">
                                            <p className="text-sm text-gray-500">Orders</p>
                                            <p className="text-lg font-semibold">{customerDetails.customer.orders_count}</p>
                                        </div>
                                        <div className="bg-gray-50 p-4 rounded-lg">
                                            <p className="text-sm text-gray-500">Avg. Order Value</p>
                                            <p className="text-lg font-semibold">¥{customerDetails.customer.avg_order_value}</p>
                                        </div>
                                    </div>

                                    {/* Customer Insights */}
                                    <div>
                                        <h3 className="text-lg font-semibold mb-4">Customer Insights</h3>
                                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                            <div className="bg-blue-50 p-4 rounded-lg">
                                                <p className="text-sm text-gray-600">Last Purchase</p>
                                                <p className="text-lg font-medium">{customerDetails.insights.last_purchase_days} days ago</p>
                                            </div>
                                            <div className="bg-blue-50 p-4 rounded-lg">
                                                <p className="text-sm text-gray-600">Favorite Brands</p>
                                                <div className="flex flex-wrap gap-1 mt-1">
                                                    {customerDetails.insights.favorite_brands.map((item, index) => (
                                                        <span
                                                            key={index}
                                                            className="px-2 py-1 bg-blue-100 text-blue-700 text-xs rounded-full"
                                                        >
                                                            {item.brand} ({item.orders})
                                                        </span>
                                                    ))}
                                                </div>
                                            </div>
                                        </div>
                                    </div>

                                    {/* Recent Purchases */}
                                    <div>
                                        <h3 className="text-lg font-semibold mb-4">Recent Purchases</h3>
                                        <div className="overflow-x-auto">
                                            <table className="min-w-full divide-y divide-gray-200">
                                                <thead className="bg-gray-50">
                                                    <tr>
                                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                                            Order ID
                                                        </th>
                                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                                            Date
                                                        </th>
                                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                                            Amount
                                                        </th>
                                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                                            Items
                                                        </th>
                                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                                            Status
                                                        </th>
                                                    </tr>
                                                </thead>
                                                <tbody className="bg-white divide-y divide-gray-200">
                                                    {customerDetails.recent_purchases.map((purchase) => (
                                                        <tr key={purchase.id}>
                                                            <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                                                                {purchase.id}
                                                            </td>
                                                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                                                {formatDate(purchase.date)}
                                                            </td>
                                                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                                                ${purchase.amount}
                                                            </td>
                                                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                                                {purchase.items_count}
                                                            </td>
                                                            <td className="px-6 py-4 whitespace-nowrap">
                                                                <span className={`px-2 py-1 text-xs rounded-full ${purchase.status === 'completed'
                                                                    ? 'bg-green-100 text-green-800'
                                                                    : 'bg-red-100 text-red-800'
                                                                    }`}>
                                                                    {purchase.status}
                                                                </span>
                                                            </td>
                                                        </tr>
                                                    ))}
                                                </tbody>
                                            </table>
                                        </div>
                                    </div>
                                </div>
                            )}

                            {activeTab === 'purchases' && (
                                <div className="space-y-6">
                                    <div className="overflow-x-auto">
                                        <table className="min-w-full divide-y divide-gray-200">
                                            <thead className="bg-gray-50">
                                                <tr>
                                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                                        Order ID
                                                    </th>
                                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                                        Date
                                                    </th>
                                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                                        Amount
                                                    </th>
                                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                                        Items
                                                    </th>
                                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                                        Brands
                                                    </th>
                                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                                        Status
                                                    </th>
                                                </tr>
                                            </thead>
                                            <tbody className="bg-white divide-y divide-gray-200">
                                                {customerDetails.recent_purchases.map((purchase) => (
                                                    <tr key={purchase.id}>
                                                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                                                            {purchase.id}
                                                        </td>
                                                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                                            {formatDate(purchase.date)}
                                                        </td>
                                                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                                            ${purchase.amount}
                                                        </td>
                                                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                                            {purchase.items_count}
                                                        </td>
                                                        <td className="px-6 py-4 whitespace-nowrap">
                                                            <div className="flex flex-wrap gap-1">
                                                                {purchase.brands.map((brand, idx) => (
                                                                    <span
                                                                        key={idx}
                                                                        className="px-2 py-1 bg-gray-100 text-gray-700 text-xs rounded-full"
                                                                    >
                                                                        {brand}
                                                                    </span>
                                                                ))}
                                                            </div>
                                                        </td>
                                                        <td className="px-6 py-4 whitespace-nowrap">
                                                            <span className={`px-2 py-1 text-xs rounded-full ${purchase.status === 'completed'
                                                                ? 'bg-green-100 text-green-800'
                                                                : 'bg-red-100 text-red-800'
                                                                }`}>
                                                                {purchase.status}
                                                            </span>
                                                        </td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>
                                </div>
                            )}

                            {activeTab === 'analytics' && (
                                <div className="space-y-6">
                                    <h3 className="text-lg font-semibold mb-4">Monthly Spending</h3>
                                    <div className="bg-white p-4 rounded-lg shadow">
                                        <div className="h-64 flex items-end space-x-2">
                                            {Object.entries(customerDetails.insights.monthly_spending).map(([month, amount]) => {
                                                const maxAmount = Math.max(...Object.values(customerDetails.insights.monthly_spending));
                                                const height = (amount / maxAmount) * 100;

                                                return (
                                                    <div key={month} className="flex flex-col items-center flex-1">
                                                        <div
                                                            className="w-full bg-blue-500 rounded-t"
                                                            style={{ height: `${height}%` }}
                                                        ></div>
                                                        <div className="text-xs mt-2 text-gray-600">{month}</div>
                                                        <div className="text-xs font-medium">${amount}</div>
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    </div>
                                </div>
                            )}
                        </>
                    ) : (
                        <div className="text-center py-12">
                            <p className="text-gray-600">Failed to load customer details</p>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}

// Customer Analytics Component
function CustomerAnalytics({ analytics }) {
    return (
        <div className="space-y-6">
            {/* Summary Stats */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
                <div className="bg-white p-6 rounded-xl shadow-lg">
                    <div className="flex items-center">
                        <UserGroupIcon className="w-8 h-8 text-blue-600 mr-3" />
                        <div>
                            <div className="text-2xl font-bold text-gray-900">{analytics.summary.total_customers}</div>
                            <div className="text-sm text-gray-600">Total Customers</div>
                        </div>
                    </div>
                </div>
                <div className="bg-white p-6 rounded-xl shadow-lg">
                    <div className="flex items-center">
                        <CurrencyDollarIcon className="w-8 h-8 text-green-600 mr-3" />
                        <div>
                            <div className="text-2xl font-bold text-gray-900">${analytics.summary.total_revenue}</div>
                            <div className="text-sm text-gray-600">Total Revenue</div>
                        </div>
                    </div>
                </div>
                <div className="bg-white p-6 rounded-xl shadow-lg">
                    <div className="flex items-center">
                        <ShoppingBagIcon className="w-8 h-8 text-purple-600 mr-3" />
                        <div>
                            <div className="text-2xl font-bold text-gray-900">¥{analytics.summary.avg_order_value}</div>
                            <div className="text-sm text-gray-600">Avg. Order Value</div>
                        </div>
                    </div>
                </div>
                <div className="bg-white p-6 rounded-xl shadow-lg">
                    <div className="flex items-center">
                        <ArrowTrendingUpIcon className="w-8 h-8 text-orange-600 mr-3" />
                        <div>
                            <div className="text-2xl font-bold text-gray-900">{analytics.summary.active_customers}</div>
                            <div className="text-sm text-gray-600">Active Customers</div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Customer Lifecycle */}
            <div className="bg-white p-6 rounded-xl shadow-lg">
                <h3 className="text-lg font-semibold mb-4">Customer Lifecycle</h3>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    <div className="bg-green-50 p-4 rounded-lg text-center">
                        <div className="text-2xl font-bold text-green-600">{analytics.customer_lifecycle.new}</div>
                        <div className="text-sm text-gray-600">New</div>
                    </div>
                    <div className="bg-blue-50 p-4 rounded-lg text-center">
                        <div className="text-2xl font-bold text-blue-600">{analytics.customer_lifecycle.regular}</div>
                        <div className="text-sm text-gray-600">Regular</div>
                    </div>
                    <div className="bg-purple-50 p-4 rounded-lg text-center">
                        <div className="text-2xl font-bold text-purple-600">{analytics.customer_lifecycle.vip}</div>
                        <div className="text-sm text-gray-600">VIP</div>
                    </div>
                    <div className="bg-gray-50 p-4 rounded-lg text-center">
                        <div className="text-2xl font-bold text-gray-600">{analytics.customer_lifecycle.inactive}</div>
                        <div className="text-sm text-gray-600">Inactive</div>
                    </div>
                </div>
            </div>

            {/* Monthly Trends */}
            <div className="bg-white p-6 rounded-xl shadow-lg">
                <h3 className="text-lg font-semibold mb-4">Monthly Trends</h3>
                <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200">
                        <thead className="bg-gray-50">
                            <tr>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    Month
                                </th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    New Customers
                                </th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    Revenue
                                </th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    Orders
                                </th>
                            </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200">
                            {analytics.monthly_trends.map((month) => (
                                <tr key={month.month}>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                                        {month.month}
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                        {month.new_customers}
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                        ${month.revenue}
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                        {month.orders}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>

            {/* Brand Preferences */}
            <div className="bg-white p-6 rounded-xl shadow-lg">
                <h3 className="text-lg font-semibold mb-4">Brand Preferences</h3>
                <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
                    {Object.entries(analytics.brand_preferences).map(([brand, count]) => (
                        <div key={brand} className="bg-gray-50 p-4 rounded-lg text-center">
                            <div className="text-lg font-bold text-gray-900">{count}</div>
                            <div className="text-sm text-gray-600">{brand}</div>
                        </div>
                    ))}
                </div>
            </div>
        </div>
    );
}

// Customer Segments Component
function CustomerSegments({ segments }) {
    return (
        <div className="space-y-6">
            <h3 className="text-lg font-semibold">Customer Segments</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                {Object.entries(segments).map(([segment, data]) => (
                    <div key={segment} className="bg-white p-6 rounded-xl shadow-lg">
                        <div className="flex items-center justify-between mb-4">
                            <h4 className="text-lg font-semibold text-gray-900">{segment}</h4>
                            <span className={`px-3 py-1 rounded-full text-xs font-medium ${segment === 'VIP' ? 'bg-purple-100 text-purple-800' :
                                segment === 'Regular' ? 'bg-blue-100 text-blue-800' :
                                    segment === 'New' ? 'bg-green-100 text-green-800' :
                                        'bg-gray-100 text-gray-800'
                                }`}>
                                {data.count} customers
                            </span>
                        </div>
                        <div className="space-y-3">
                            <div className="flex justify-between">
                                <span className="text-sm text-gray-500">Avg. Order Value</span>
                                <span className="text-sm font-medium">¥{data.avg_order_value}</span>
                            </div>
                            <div className="flex justify-between">
                                <span className="text-sm text-gray-500">Avg. Orders</span>
                                <span className="text-sm font-medium">{data.avg_orders}</span>
                            </div>
                            <div className="flex justify-between">
                                <span className="text-sm text-gray-500">Revenue/Customer</span>
                                <span className="text-sm font-medium">${data.revenue_per_customer}</span>
                            </div>
                            <div className="flex justify-between">
                                <span className="text-sm text-gray-500">Total Revenue</span>
                                <span className="text-sm font-medium">${data.total_revenue}</span>
                            </div>
                        </div>
                    </div>
                ))}
            </div>
        </div>
    );
}

// Main Customers Component
export default function Customers() {
    const [customers, setCustomers] = useState([]);
    const [overview, setOverview] = useState(null);
    const [segments, setSegments] = useState(null);
    const [analytics, setAnalytics] = useState(null);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    const [sortBy, setSortBy] = useState('last_purchase');
    const [sortOrder, setSortOrder] = useState('desc');
    const [currentPage, setCurrentPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [selectedCustomer, setSelectedCustomer] = useState(null);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [activeTab, setActiveTab] = useState('customers');
    const [selectedSegment, setSelectedSegment] = useState('');
    const [analyticsPeriod, setAnalyticsPeriod] = useState('30d');

    const itemsPerPage = 12;

    useEffect(() => {
        fetchCustomers();
        fetchOverview();
        fetchSegments();
        fetchAnalytics();
    }, [currentPage, sortBy, sortOrder, searchTerm, selectedSegment, analyticsPeriod]);

    const fetchCustomers = async () => {
        setLoading(true);
        try {
            const params = new URLSearchParams({
                page: currentPage.toString(),
                limit: itemsPerPage.toString(),
                sort_by: sortBy,
                sort_order: sortOrder,
                ...(searchTerm && { search: searchTerm }),
                ...(selectedSegment && { segment: selectedSegment })
            });

            const backendUrl = process.env.REACT_APP_BACKEND2_BASE_URL || 'https://suanfamama-fashion-buyer-production.up.railway.app';
            // call the trailing-slash path directly: /customers 307s to an http:// URL,
              // which browsers block as mixed content on an https page
              const response = await fetch(`${backendUrl}/customers/?${params}`);
            if (response.ok) {
                const data = await response.json();
                setCustomers(data.customers || []);
                setTotalPages(data.total_pages || 1);
            }
        } catch (error) {
            console.error('Error fetching customers:', error);
        } finally {
            setLoading(false);
        }
    };

    const fetchOverview = async () => {
        try {
            const backendUrl = process.env.REACT_APP_BACKEND2_BASE_URL || 'https://suanfamama-fashion-buyer-production.up.railway.app';
            const response = await fetch(`${backendUrl}/customers/overview`);
            if (response.ok) {
                const data = await response.json();
                setOverview(data);
            }
        } catch (error) {
            console.error('Error fetching overview:', error);
        }
    };

    const fetchSegments = async () => {
        try {
            const backendUrl = process.env.REACT_APP_BACKEND2_BASE_URL || 'https://suanfamama-fashion-buyer-production.up.railway.app';
            const response = await fetch(`${backendUrl}/customers/segments`);
            if (response.ok) {
                const data = await response.json();
                setSegments(data);
            }
        } catch (error) {
            console.error('Error fetching segments:', error);
        }
    };

    const fetchAnalytics = async () => {
        try {
            const params = new URLSearchParams({
                period: analyticsPeriod,
                ...(selectedSegment && { segment: selectedSegment })
            });

            const backendUrl = process.env.REACT_APP_BACKEND2_BASE_URL || 'https://suanfamama-fashion-buyer-production.up.railway.app';
            const response = await fetch(`${backendUrl}/customers/analytics?${params}`);
            if (response.ok) {
                const data = await response.json();
                setAnalytics(data);
            }
        } catch (error) {
            console.error('Error fetching analytics:', error);
        }
    };

    const handleSearch = (e) => {
        setSearchTerm(e.target.value);
        setCurrentPage(1);
    };

    const handleSortChange = (newSortBy) => {
        if (sortBy === newSortBy) {
            setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
        } else {
            setSortBy(newSortBy);
            setSortOrder('desc');
        }
        setCurrentPage(1);
    };

    const handleSegmentChange = (segment) => {
        setSelectedSegment(segment === 'All' ? '' : segment);
        setCurrentPage(1);
    };

    const handlePeriodChange = (period) => {
        setAnalyticsPeriod(period);
    };

    const handleViewDetails = (customer) => {
        setSelectedCustomer(customer);
        setIsModalOpen(true);
    };

    const handlePageChange = (page) => {
        setCurrentPage(page);
        window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    if (loading && !overview && !segments && !analytics) {
        return (
            <div className="flex items-center justify-center py-12">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
            </div>
        );
    }

    return (
        <div className="space-y-6">
            {/* Tabs */}
            <div className="bg-white rounded-xl shadow-lg p-4">
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                    <div className="flex space-x-4">
                        <button
                            onClick={() => setActiveTab('customers')}
                            className={`px-4 py-2 rounded-lg font-medium transition-colors ${activeTab === 'customers'
                                ? 'bg-blue-600 text-white'
                                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                                }`}
                        >
                            Customers
                        </button>
                        <button
                            onClick={() => setActiveTab('analytics')}
                            className={`px-4 py-2 rounded-lg font-medium transition-colors ${activeTab === 'analytics'
                                ? 'bg-blue-600 text-white'
                                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                                }`}
                        >
                            Analytics
                        </button>
                        <button
                            onClick={() => setActiveTab('segments')}
                            className={`px-4 py-2 rounded-lg font-medium transition-colors ${activeTab === 'segments'
                                ? 'bg-blue-600 text-white'
                                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                                }`}
                        >
                            Segments
                        </button>
                    </div>

                    {activeTab === 'analytics' && (
                        <div className="flex items-center space-x-2">
                            <span className="text-sm text-gray-600">Period:</span>
                            <select
                                value={analyticsPeriod}
                                onChange={(e) => handlePeriodChange(e.target.value)}
                                className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                            >
                                <option value="7d">Last 7 days</option>
                                <option value="30d">Last 30 days</option>
                                <option value="90d">Last 90 days</option>
                                <option value="1y">Last year</option>
                            </select>
                        </div>
                    )}
                </div>
            </div>

            {/* Customers List */}
            {activeTab === 'customers' && (
                <>
                    {/* Search and Filters */}
                    <div className="bg-white p-6 rounded-xl shadow-lg">
                        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
                            {/* Search */}
                            <div className="relative flex-1 max-w-md">
                                <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                                <input
                                    type="text"
                                    placeholder="Search customers..."
                                    value={searchTerm}
                                    onChange={handleSearch}
                                    className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                />
                            </div>

                            {/* Filters */}
                            <div className="flex flex-wrap gap-2">
                                <div className="relative">
                                    <select
                                        value={selectedSegment}
                                        onChange={(e) => handleSegmentChange(e.target.value)}
                                        className="pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent appearance-none"
                                    >
                                        <option value="">All Segments</option>
                                        <option value="VIP">VIP</option>
                                        <option value="Regular">Regular</option>
                                        <option value="New">New</option>
                                        <option value="Inactive">Inactive</option>
                                    </select>
                                    <FunnelIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                                </div>

                                {/* Sort Options */}
                                <button
                                    onClick={() => handleSortChange('last_purchase')}
                                    className={`px-4 py-2 rounded-lg font-medium transition-colors flex items-center ${sortBy === 'last_purchase'
                                        ? 'bg-blue-600 text-white'
                                        : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                                        }`}
                                >
                                    Last Purchase
                                    {sortBy === 'last_purchase' && (
                                        sortOrder === 'asc'
                                            ? <ChevronUpIcon className="w-4 h-4 ml-1" />
                                            : <ChevronDownIcon className="w-4 h-4 ml-1" />
                                    )}
                                </button>
                                <button
                                    onClick={() => handleSortChange('total_spent')}
                                    className={`px-4 py-2 rounded-lg font-medium transition-colors flex items-center ${sortBy === 'total_spent'
                                        ? 'bg-blue-600 text-white'
                                        : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                                        }`}
                                >
                                    Spending
                                    {sortBy === 'total_spent' && (
                                        sortOrder === 'asc'
                                            ? <ChevronUpIcon className="w-4 h-4 ml-1" />
                                            : <ChevronDownIcon className="w-4 h-4 ml-1" />
                                    )}
                                </button>
                            </div>
                        </div>
                    </div>

                    {/* Customers Grid */}
                    {customers.length === 0 ? (
                        <div className="text-center py-12">
                            <UserIcon className="w-16 h-16 text-gray-400 mx-auto mb-4" />
                            <h3 className="text-lg font-medium text-gray-900 mb-2">No customers found</h3>
                            <p className="text-gray-600">Try adjusting your search criteria</p>
                        </div>
                    ) : (
                        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
                            {customers.map((customer) => (
                                <CustomerCard
                                    key={customer.id}
                                    customer={customer}
                                    onViewDetails={handleViewDetails}
                                />
                            ))}
                        </div>
                    )}

                    {/* Pagination */}
                    {totalPages > 1 && (
                        <div className="flex items-center justify-center space-x-2">
                            <button
                                onClick={() => handlePageChange(currentPage - 1)}
                                disabled={currentPage === 1}
                                className={`px-4 py-2 rounded-lg font-medium ${currentPage === 1
                                    ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                                    : 'bg-white text-gray-700 hover:bg-gray-50 border border-gray-300'
                                    }`}
                            >
                                Previous
                            </button>

                            {[...Array(Math.min(totalPages, 5))].map((_, index) => {
                                const page = index + 1;
                                return (
                                    <button
                                        key={page}
                                        onClick={() => handlePageChange(page)}
                                        className={`px-4 py-2 rounded-lg font-medium ${currentPage === page
                                            ? 'bg-blue-600 text-white'
                                            : 'bg-white text-gray-700 hover:bg-gray-50 border border-gray-300'
                                            }`}
                                    >
                                        {page}
                                    </button>
                                );
                            })}

                            <button
                                onClick={() => handlePageChange(currentPage + 1)}
                                disabled={currentPage === totalPages}
                                className={`px-4 py-2 rounded-lg font-medium ${currentPage === totalPages
                                    ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                                    : 'bg-white text-gray-700 hover:bg-gray-50 border border-gray-300'
                                    }`}
                            >
                                Next
                            </button>
                        </div>
                    )}
                </>
            )}

            {/* Analytics Tab */}
            {activeTab === 'analytics' && analytics && (
                <CustomerAnalytics analytics={analytics} />
            )}

            {/* Segments Tab */}
            {activeTab === 'segments' && segments && (
                <CustomerSegments segments={segments} />
            )}

            {/* Customer Details Modal */}
            <CustomerDetailsModal
                customer={selectedCustomer}
                isOpen={isModalOpen}
                onClose={() => setIsModalOpen(false)}
            />
        </div>
    );
}
